import { ContactlessOutlined } from '@mui/icons-material';
import API from '../api/api';
import { syncProducts } from './products';
import { syncCollections } from './collections';
import { syncPages } from './pages';
import { syncNavigation } from './navigation';
import { syncBlogs } from './blogs';
import { syncArticles } from './articles';
import { getTranslationProgress } from './stats';
import { refreshAvailableWords } from './shop';

const COUNT_WORDS_PENDING = 'COUNT_WORDS_PENDING';
const COUNT_WORDS_PROGRESS = 'COUNT_WORDS_PROGRESS';
const COUNT_WORDS_RESPONSE = 'COUNT_WORDS_RESPONSE';
const TRANSLATE_WORDS_PENDING = 'TRANSLATE_WORDS_PENDING';
const TRANSLATE_WORDS_PROGRESS = 'TRANSLATE_WORDS_PROGRESS';
const TRANSLATE_WORDS_RESPONSE = 'TRANSLATE_WORDS_RESPONSE';
const RESET_ERRORS = 'RESET_ERRORS';
const GET_RUNNING_TASKS_PENDING = 'GET_RUNNING_TASKS_PENDING';
const GET_RUNNING_TASKS_RESPONSE = 'GET_RUNNING_TASKS_RESPONSE';
const CLEAR_TASKS_PENDING = 'CLEAR_TASKS_PENDING';
const CLEAR_TASKS_RESPONSE = 'CLEAR_TASKS_RESPONSE';
const PREPARE_COUNT_WORDS = 'PREPARE_COUNT_WORDS';
const REWIND_BULK_SETTINGS = 'REWIND_BULK_SETTINGS';
const RESET_RUN = 'RESET_RUN';
const SET_SYNCHRONIZING = 'SET_SYNCHRONIZING';
const SECTION_SYNCHRONIZED = 'SECTION_SYNCHRONIZED';

const initialState = {
  prepare_word_count_pending: false,
  count_words_pending: false,
  count_progress: 0,
  count_tasks: {},
  all_count_jobs_count: 0,
  all_count_jobs_done: 0,
  translate_words_pending: false,
  translate_progress: 0,
  translate_tasks: {},
  all_translate_jobs_count: 0,
  all_translate_jobs_done: 0,
  total_words: 0,
  error: false,
  task_errors: [],
  bulk_rewind_settings: false,
  unfinished_process: false,
  is_task_querying: false,
  tasks: {},
  tasks_canceled: false,
  is_synchronizing: false,
  // 'before': the selection is synced ahead of a count or run; 'after': a finished run is
  // pulled back from Shopify. The progress line words them differently
  sync_phase: null,
  // How many section syncs the running phase covers, so the dialog can say 2 of 5 instead of
  // just "Synchronizing...". Both phases feed these: the one before counting and the one that
  // syncs a finished run back.
  sync_total: 0,
  sync_done: 0,
}

const sumBy = (obj, field) => obj
  .map(item => {
    if(!item[1]) return null;
    return item[1][field];
  })
  .reduce((prev, curr) => prev + curr, 0);

const concatBy = (obj, field) => obj
  .map(item => {
    if(!item.task) return null;
    return item.task[field];
  })
  .reduce((prev, curr) => prev.concat(curr), []);

const taskKey2DataObject = (key) => {
  let split1 = key.split(':');
  let split2 = split1[2].split('_')
  return {
    store: split1[1],
    to: split2[0],
    scope: split2[1].toLowerCase(),
    type: split2[2].toLowerCase(),
  };
}

// REDUCER
export default (state = initialState, action) => {
  switch(action.type) {
    case RESET_ERRORS: {
      return {
        ...state,
        error: false,
      }
    }
    case PREPARE_COUNT_WORDS: {
      return {
        ...state,
        prepare_word_count_pending: true,
        bulk_rewind_settings: false,
        total_words: 0,
        count_progress: 0,
        count_tasks: {},
        translate_progress: 0,
        translate_tasks: {},
        tasks: {}
      }
    }
    case COUNT_WORDS_PENDING: {
      return {
        ...state,
        prepare_word_count_pending: false,
        count_words_pending: true,
      }
    }
    case COUNT_WORDS_RESPONSE: {
      if(action.response.success === true) {
        return {
          ...state,
        }
      } else {
        return {
          ...state,
          //count_words_pending: false,
          error: {
            message: action.error,
            level: 0,
          },
        }
      }
    }



    case TRANSLATE_WORDS_PENDING: {
      return {
        ...state,
        translate_progress: 0,
        translate_words_pending: true,
        count_words_pending: false,
        error: false,
        task_errors: [],
        unfinished_process: true,
      }
    }
    case TRANSLATE_WORDS_RESPONSE: {
      if(action.response.success === true) {
        return {
          ...state,
        }
      } else {
        return {
          ...state,
          //translate_words_pending: false,
          error: {
            message: action.response,
            action: action.action,
            level: 0,
          },
        }
      }
    }


    case GET_RUNNING_TASKS_RESPONSE: {
      if(state.prepare_word_count_pending) {
        return {
          ...state
        }
      }


      // Update the countProcesses array
      let countTasks = {};
      let translateTasks = {};
      let new_is_task_querying = false;
      const responseTasks = (action.response.success && action.response.tasks) ? action.response.tasks : {};

      for(let [task_key, task_value] of Object.entries(responseTasks)) {
        // 1. Seperate count and translate tasks
        if(task_key.indexOf('_COUNT') > 0) {
          countTasks[task_key] = task_value;
          if(task_value.bulk_operation) {
            if(task_value.bulk_operation.type === 'QUERY' && task_value.bulk_operation.status === 'RUNNING') {
              new_is_task_querying = true;
            }
          }
        }
        if(task_key.indexOf('_TRANSLATE') > 0) {
          translateTasks[task_key] = task_value;
        }
      }

      
      let all_total_words = sumBy(Object.entries(countTasks), 'WordCount');
      let all_count_jobs_done = sumBy(Object.entries(countTasks), 'jobs_done');
      let all_count_jobs_count = sumBy(Object.entries(countTasks), 'jobs_count');
      let new_count_progress = Math.round(all_count_jobs_done / all_count_jobs_count * 100);
      let new_count_words_pending = false;
      
      let all_translate_jobs_done = sumBy(Object.entries(translateTasks), 'jobs_done');
      let all_translate_jobs_count = sumBy(Object.entries(translateTasks), 'jobs_count');
      let new_translate_progress = Math.round(all_translate_jobs_done / all_translate_jobs_count * 100);
      let new_translate_words_pending = false;
      
      if(Object.values(countTasks).find(x => x.status === 'running')) {
        new_count_words_pending = true;
      }
      if(Object.values(translateTasks).find(x => x.status === 'running')) {
        new_translate_words_pending = true;
      }

      let new_unfinished_process = false;
      if(Object.values(translateTasks).length > 0 || window.localStorage.getItem('ly_bulk_translation:out_of_sync')) {
        new_unfinished_process = true;
      }

      return {
        ...state,
        total_words: all_total_words,
        count_tasks: countTasks,
        count_progress: new_count_progress ? new_count_progress : 0,
        count_words_pending: new_count_words_pending,
        translate_tasks: translateTasks,
        translate_progress: new_translate_progress ? new_translate_progress : 0,
        translate_words_pending: new_translate_words_pending,
        all_count_jobs_count: all_count_jobs_count,
        all_count_jobs_done: all_count_jobs_done,
        all_translate_jobs_count: all_translate_jobs_count,
        all_translate_jobs_done: all_translate_jobs_done,
        unfinished_process: new_unfinished_process,
        is_task_querying: new_is_task_querying,
        tasks: responseTasks,
        tasks_canceled: false,
        //task_errors: all_errors,
      }
    }

    case CLEAR_TASKS_RESPONSE: {
      return {
        ...state,
        total_words: 0,
        count_words_pending: false,
        count_progress: 0,
        count_tasks: {},
        translate_words_pending: false,
        translate_progress: 0,
        translate_tasks: {},
        all_count_jobs_count: 0,
        all_count_jobs_done: 0,
        all_translate_jobs_count: 0,
        all_translate_jobs_done: 0,
        tasks: {},
        tasks_canceled: true,
      }
    }

    case RESET_RUN: {
      return {
        ...state,
        total_words: 0,
        count_words_pending: false,
        count_progress: 0,
        count_tasks: {},
        translate_words_pending: false,
        translate_progress: 0,
        translate_tasks: {},
        all_count_jobs_count: 0,
        all_count_jobs_done: 0,
        all_translate_jobs_count: 0,
        all_translate_jobs_done: 0,
        task_errors: [],
        tasks: {},
        unfinished_process: false,
      }
    }

    case REWIND_BULK_SETTINGS: {
      return {
        ...state,
        bulk_rewind_settings: true,
        unfinished_process: false,
        tasks: {},
      }
    }

    case SET_SYNCHRONIZING: {
      return {
        ...state,
        is_synchronizing: action.value,
        sync_phase: action.value ? (action.phase || 'before') : null,
        sync_total: action.value ? (action.total || 0) : 0,
        sync_done: 0,
      }
    }

    case SECTION_SYNCHRONIZED: {
      return {
        ...state,
        sync_done: Math.min(state.sync_done + 1, state.sync_total || state.sync_done + 1),
      }
    }

    default:
      return state
  }
}

// ACTIONS
export const resetErrors = () => {
  return dispatch => {
    dispatch({
      type: RESET_ERRORS
    });
  }
}
export const prepareCountWords = () => {
  return dispatch => {
    dispatch({
      type: PREPARE_COUNT_WORDS
    });
  }
}
export const bulkCountWords = (store, from, to, scope, subObject, gids, outdated, callback) => {
  return dispatch => {
    dispatch({
      type: COUNT_WORDS_PENDING,
      store: store,
      from: from,
      to: to,
      scope: scope
    })

    API.bulkCountStart(
      store, 
      from, 
      to,
      scope,
      subObject,
      gids,
      outdated,
      response => {
        dispatch({
          type: COUNT_WORDS_RESPONSE,
          response: response,
          scope: scope
        })
        callback(response);
    })
  }
}
export const bulkTranslateDirect = (store, from, to, scope, subObject, gids, outdated, callback) => {
  return dispatch => {
    dispatch({
      type: TRANSLATE_WORDS_PENDING,
      store: store,
      from: from,
      to: to,
      scope: scope
    })

    API.bulkTranslateDirectStart(
      store,
      from,
      to,
      scope,
      subObject,
      gids,
      outdated,
      response => {
        // The AI run skips the count step, so there is no task key to mark upfront, and the
        // key in this response is the queue's, not the one running_tasks reports. The run's
        // language and scope are known here though — mark them so the level-triggered sync
        // below picks the run up once it has finished.
        if(response?.success) {
          markScopesOutOfSync(to, scope);
        }
        dispatch({
          type: TRANSLATE_WORDS_RESPONSE,
          response: response,
        })
        callback(response);
    })
  }
}
// The sections of a finished run have to be pulled back from Shopify. The trigger must
// not live in a component: a run usually ends while the user is on some other page, and
// the edge-triggered version in the bulk dialog then never fired at all.
const OUT_OF_SYNC_KEY = 'ly_bulk_translation:out_of_sync';

const SECTION_SYNC_ACTIONS = {
  products:    (languageId, done) => syncProducts(languageId, true, done, {held: true}),
  collections: (languageId, done) => syncCollections(languageId, true, done, {held: true}),
  pages:       (languageId, done) => syncPages(languageId, true, done, {held: true}),
  navigation:  (languageId, done) => syncNavigation(languageId, done, {held: true}),
  blogs:       (languageId, done) => syncBlogs(languageId, done, {held: true}),
  articles:    (languageId, done) => syncArticles(languageId, true, done, {held: true}),
};

const parseTaskKey = (key) => {
  try {
    return taskKey2DataObject(key);
  } catch(e) {
    return {};
  }
}

const readOutOfSyncSections = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(OUT_OF_SYNC_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch(e) {
    return [];
  }
}

const writeOutOfSyncSections = (sections) => {
  if(sections.length === 0) {
    window.localStorage.removeItem(OUT_OF_SYNC_KEY);
    return;
  }
  window.localStorage.setItem(OUT_OF_SYNC_KEY, JSON.stringify(sections));
}

// A task key carries every scope of its run as a comma separated list
// (e.g. "es_COLLECTIONS,NAVIGATION_TRANSLATE"), so it has to be split up.
const markSectionsOutOfSync = (taskKey) => {
  const { to, scope } = parseTaskKey(taskKey);
  markScopesOutOfSync(to, scope);
}

// `to` is stored lower-cased because that is how the backend writes it into the task keys,
// and markedRunHasFinished matches the marker against those keys.
const markScopesOutOfSync = (to, scope) => {
  if(!to || !scope) return;
  to = String(to).toLowerCase();

  const sections = readOutOfSyncSections();
  for(const rawScope of scope.split(',')) {
    const single = rawScope.trim();
    // 'none' is the themes placeholder — themes are not synced section-wise
    if(!single || !SECTION_SYNC_ACTIONS[single]) continue;
    if(!sections.find(s => s.scope === single && s.to === to)) {
      sections.push({ scope: single, to });
    }
  }
  writeOutOfSyncSections(sections);
}

// True once a translate task covering the marked sections has stopped running. Keeps the
// sync from firing in the gap between starting a run and the task showing up as running.
const markedRunHasFinished = (tasks, sections) => {
  let running = false;
  let finished = false;
  for(const [taskKey, task] of Object.entries(tasks || {})) {
    if(task?.type !== 'translate') continue;
    const { to, scope } = parseTaskKey(taskKey);
    if(!to || !scope) continue;
    const covers = scope.split(',').some(rawScope => sections.find(s => s.scope === rawScope.trim() && s.to === to));
    if(!covers) continue;
    if(task.status === 'running') running = true;
    else finished = true;
  }
  return finished && !running;
}

// The queue counts a finished worker job per gid under the resource's own key, so a section's
// touches spread over more than one key (a product run also writes options and option values).
const WORKER_COUNT_KEYS = {
  products:   ['products', 'options', 'option_value'],
  navigation: ['navigations'],
};

const ABORTED_STATES = ['cancelled', 'canceled', 'failed', 'error'];

// A section every covering task aborted without one finished worker job was never written to,
// so a cancel needs no resync of it. The worker count also includes jobs that failed, which is
// the safe side: skipped only when the queue never touched the section. A completed task keeps
// the section as it is: it syncs like before.
const untouchedByAbortedRun = (tasks, section) => {
  let covered = false;
  for(const [taskKey, task] of Object.entries(tasks || {})) {
    if(task?.type !== 'translate') continue;
    const { to, scope } = parseTaskKey(taskKey);
    if(to !== section.to || !scope) continue;
    if(!scope.split(',').some(rawScope => rawScope.trim() === section.scope)) continue;
    covered = true;
    if(!ABORTED_STATES.includes(task.status)) return false;
    const worker = task.detail_count?.worker || {};
    const keys = WORKER_COUNT_KEYS[section.scope] || [section.scope];
    if(keys.some(key => worker[key] > 0)) return false;
  }
  return covered;
}

let syncFinishedSectionsPending = false;

// Level-triggered: re-checked on every tasks response until the marker is empty, so a run
// that finished while the user was on another page or before a reload is still synced.
export const bulkTranslationSyncFinishedSections = (tasks) => {
  return async (dispatch, getState) => {
    if(syncFinishedSectionsPending) return;

    let sections = readOutOfSyncSections();
    if(sections.length === 0) return;
    if(!markedRunHasFinished(tasks, sections)) return;

    sections = sections.filter(section => !untouchedByAbortedRun(tasks, section));
    if(sections.length === 0) {
      writeOutOfSyncSections([]);
      return;
    }

    syncFinishedSectionsPending = true;
    dispatch({
      type: SET_SYNCHRONIZING,
      value: true,
      total: sections.length,
      phase: 'after',
    });

    const state = getState();
    const languages = state.languages?.languages || [];
    const retry = [];
    let synced = 0;

    try {
      await Promise.all(sections.map(section => new Promise(resolve => {
        const run = SECTION_SYNC_ACTIONS[section.scope];
        if(!run) {
          dispatch({type: SECTION_SYNCHRONIZED});
          resolve();
          return;
        }
        const language = languages.find(x => x.code.toLowerCase() === String(section.to).toLowerCase());
        if(!language) {
          // Languages are not in the store yet (e.g. right after a reload) — keep the marker
          retry.push(section);
          dispatch({type: SECTION_SYNCHRONIZED});
          resolve();
          return;
        }
        synced++;
        dispatch(run(language.id, () => {
          dispatch({type: SECTION_SYNCHRONIZED});
          resolve();
        }));
      })));
    } finally {
      writeOutOfSyncSections(retry);
      syncFinishedSectionsPending = false;
      dispatch({
        type: SET_SYNCHRONIZING,
        value: false,
      });
    }

    if(synced > 0) {
      dispatch(getTranslationProgress(state.shop?.shop?.edit_language, state.themes?.edit_theme));
    }
    // The finished run's real usage can differ from what was deducted upfront
    dispatch(refreshAvailableWords());
  }
}

export const bulkTranslationStart = (store, tasks, callback) => {  
  return async dispatch => {

    for(const entry of Object.entries(tasks)) {

      if(entry[1].WordCount > 0) {
        dispatch({
          type: TRANSLATE_WORDS_PENDING,
          store: store,
          key: entry[1].key,
        })
        markSectionsOutOfSync(entry[0]);
    
        await API.bulkTranslationStart(
          store, 
          entry[1].key,
          response => {
            dispatch({
              type: TRANSLATE_WORDS_RESPONSE,
              response: response,
            });
            callback(response);
        })
      } else {
        await API.bulkTranslationClearTask(entry[0]);
      }
    }

    // The words are deducted when the run starts, so pull the balance once more.
    dispatch(refreshAvailableWords());
  }
}
// The backend keeps returning expired tasks. Drop them here so no consumer sees them:
// they would otherwise render as pending rows and keep a run marked as unfinished.
const withoutExpiredTasks = (response) => {
  if(!response || !response.tasks) return response;
  const tasks = Object.fromEntries(
    Object.entries(response.tasks).filter(([, task]) => task?.status !== 'expired')
  );
  return { ...response, tasks };
}

export const bulkTranslationGetTasks = (callback) => {
  return dispatch => {
    dispatch({
      type: GET_RUNNING_TASKS_PENDING
    });

    API.bulkTranslationGetRunningTasks(
      response => {
        const filtered = withoutExpiredTasks(response);
        dispatch({
          type: GET_RUNNING_TASKS_RESPONSE,
          response: filtered
        });
        callback(filtered);

        // Raw response on purpose: an expired run is hidden from the UI but its sections
        // still have to be pulled back from Shopify.
        dispatch(bulkTranslationSyncFinishedSections(response?.tasks));
      }
    )
  }
}
export const bulkTranslationClearTasks = (tasks, callback) => {
  return async dispatch => {
    dispatch({
      type: CLEAR_TASKS_PENDING
    });
    for(const entry of Object.entries(tasks)) {
      // clearing only dropped the API's entry: the queue kept running the task and kept Shopify's
      // one bulk query slot, so the next count with sub objects was refused by a run the merchant
      // had already cleared. Cancel first, then forget it.
      await API.bulkTranslationCancelTask(entry[0]);
      await API.bulkTranslationClearTask(entry[0]);
    }
    // Clearing a run means forgetting it, its sections included
    writeOutOfSyncSections([]);
    dispatch({
      type: CLEAR_TASKS_RESPONSE
    });
    if(typeof callback === 'function') {
      callback();
    }
  }
}
// Drops the finished run from the store. Used when the user acknowledges a result with
// "Finished" — the next configuration must not show the previous run's tasks and counts.
export const bulkTranslationResetRun = () => {
  return dispatch => {
    dispatch({
      type: RESET_RUN
    });
  }
}
export const bulkTranslationRewind = () => {
  return dispatch => {
    writeOutOfSyncSections([]);
    dispatch({
      type: REWIND_BULK_SETTINGS
    });
  }
}
export const bulkTranslationSectionSynchronized = () => {
  return dispatch => {
    dispatch({
      type: SECTION_SYNCHRONIZED,
    })
  }
}

export const bulkTranslationSetSynchronizing = (value, total, phase = 'before') => {
  return dispatch => {
    dispatch({
      type: SET_SYNCHRONIZING,
      value,
      total,
      phase,
    });
  }
}